import React, { useState, useEffect } from 'react';
import { Location } from '@/firebase/locationService';
import { floorService, Floor } from '@/firebase/floorService';
import { useNotification } from '@/contexts/NotificationContext';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useAuth } from '@/contexts/AuthContext';
import { X, Plus, Trash2, Pencil, Map, Upload } from 'lucide-react';

interface FloorManagementPanelProps {
  location: Location;
  /** Required when variant is drawer; optional for inline (shows close button when provided) */
  onClose?: () => void;
  onViewFloorMap?: (floorId: string) => void;
  /** Inline: embedded on Floor Map page instead of right drawer */
  variant?: 'drawer' | 'inline';
  /** Called after floors are added/updated/deleted or map uploaded */
  onFloorsChanged?: () => void;
}

const FloorManagementPanel: React.FC<FloorManagementPanelProps> = ({
  location,
  onClose,
  onViewFloorMap,
  variant = 'drawer',
  onFloorsChanged,
}) => {
  const isInline = variant === 'inline';
  const { showNotification } = useNotification();
  const { currentOrganization } = useOrganization();
  const { user } = useAuth();
  const [floors, setFloors] = useState<Floor[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [editingFloor, setEditingFloor] = useState<Floor | null>(null);
  const [newFloorName, setNewFloorName] = useState('');
  const [newFloorOrder, setNewFloorOrder] = useState(0);
  const [uploadingFloorId, setUploadingFloorId] = useState<string | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  useEffect(() => {
    loadFloors();
  }, [location?.id, currentOrganization?.id]);

  const loadFloors = async () => {
    if (!location?.id || !currentOrganization?.id) return;
    setIsLoading(true);
    const result = await floorService.getFloorsByLocation(location.id, currentOrganization.id);
    if (result.success && result.data) {
      setFloors(result.data);
    } else {
      setFloors([]);
    }
    setIsLoading(false);
  };

  const handleAddFloor = async () => {
    if (!newFloorName.trim() || !location?.id || !currentOrganization?.id) {
      showNotification('Please enter a floor name', 'error');
      return;
    }
    const userInfo = user ? { id: user.id, name: user.name || '', email: user.email || '' } : undefined;
    const result = await floorService.addFloor(
      { locationId: location.id, name: newFloorName.trim(), order: newFloorOrder },
      currentOrganization.id,
      userInfo
    );
    if (result.success) {
      setNewFloorName('');
      setNewFloorOrder(floors.length);
      setIsAdding(false);
      await loadFloors();
      onFloorsChanged?.();
      showNotification('Floor added successfully', 'success');
    } else {
      showNotification(`Error: ${result.error}`, 'error');
    }
  };

  const handleUpdateFloor = async (floor: Floor) => {
    if (!editingFloor?.id || !currentOrganization?.id) return;
    const userInfo = user ? { id: user.id, name: user.name || '', email: user.email || '' } : undefined;
    const result = await floorService.updateFloor(
      editingFloor.id,
      { name: editingFloor.name, order: editingFloor.order },
      userInfo,
      currentOrganization.id
    );
    if (result.success) {
      setEditingFloor(null);
      await loadFloors();
      onFloorsChanged?.();
      showNotification('Floor updated successfully', 'success');
    } else {
      showNotification(`Error: ${result.error}`, 'error');
    }
  };

  const handleDeleteFloor = async (floor: Floor) => {
    if (!floor.id || !confirm(`Delete floor "${floor.name}"? Assets tagged to this floor will lose their floor position.`)) return;
    const userInfo = user ? { id: user.id, name: user.name || '', email: user.email || '' } : undefined;
    const result = await floorService.deleteFloor(floor.id, userInfo, currentOrganization?.id);
    if (result.success) {
      await loadFloors();
      onFloorsChanged?.();
      showNotification('Floor deleted', 'success');
    } else {
      showNotification(`Error: ${result.error}`, 'error');
    }
  };

  const handleUploadFloorMap = async (floorId: string, file: File) => {
    if (!currentOrganization?.id) return;
    setUploadingFloorId(floorId);
    const result = await floorService.uploadFloorMapImage(floorId, file, currentOrganization.id);
    if (result.success && result.url) {
      const userInfo = user ? { id: user.id, name: user.name || '', email: user.email || '' } : undefined;
      await floorService.updateFloor(
        floorId,
        { floorMapImageUrl: result.url },
        userInfo,
        currentOrganization.id
      );
      await loadFloors();
      onFloorsChanged?.();
      showNotification('Floor map uploaded successfully', 'success');
    } else {
      showNotification(`Upload failed: ${result.error}`, 'error');
    }
    setUploadingFloorId(null);
    fileInputRef.current?.form?.reset();
  };

  return (
    <div
      className={
        isInline
          ? 'flex flex-col rounded-lg border border-gray-200 bg-white shadow-sm overflow-hidden min-h-0'
          : 'fixed inset-y-0 right-0 w-[480px] max-w-[100vw] bg-white shadow-2xl border-l border-gray-200 z-[120] flex flex-col'
      }
    >
      <div
        className={`flex items-center justify-between p-4 border-b border-gray-200 shrink-0 ${
          isInline ? 'bg-primary-50/80' : 'bg-gray-50'
        }`}
      >
        <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
          <Map className="w-5 h-5 text-primary-600" />
          {isInline ? 'Manage floors & floor plans' : `Floors: ${location.name}`}
        </h2>
        {onClose && (
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700 p-1" title="Close">
            <X className="w-5 h-5" />
          </button>
        )}
      </div>
      {isInline && (
        <p className="px-4 py-2 text-xs text-gray-600 border-b border-gray-100 bg-gray-50">
          Location: <span className="font-medium text-gray-800">{location.name}</span>
        </p>
      )}

      <div className={`flex-1 overflow-y-auto p-4 ${isInline ? 'max-h-[min(520px,55vh)]' : ''}`}>
        {!isInline && (
          <p className="text-sm text-gray-600 mb-4">
            Add floors and upload floor plan images. You can then tag assets to positions on each floor map.
          </p>
        )}
        {isInline && (
          <p className="text-sm text-gray-600 mb-4">
            Add floors, upload or replace floor plan images, then tag assets from Asset Management.
          </p>
        )}

        {isLoading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto" />
          </div>
        ) : (
          <>
            {floors.map((floor) => (
              <div
                key={floor.id}
                className="mb-4 p-4 border border-gray-200 rounded-lg bg-white"
              >
                {editingFloor?.id === floor.id && editingFloor ? (
                  <div className="space-y-2">
                    <input
                      type="text"
                      value={editingFloor.name}
                      onChange={(e) => setEditingFloor({ ...editingFloor, locationId: editingFloor.locationId, name: e.target.value })}
                      className="w-full px-3 py-2 border rounded text-sm"
                    />
                    <input
                      type="number"
                      value={editingFloor.order}
                      onChange={(e) => setEditingFloor({ ...editingFloor, locationId: editingFloor.locationId, order: parseInt(e.target.value, 10) || 0 })}
                      placeholder="Order"
                      className="w-20 px-2 py-1 border rounded text-sm"
                    />
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleUpdateFloor(floor)}
                        className="px-3 py-1 bg-primary-600 text-white rounded text-sm"
                      >
                        Save
                      </button>
                      <button
                        onClick={() => setEditingFloor(null)}
                        className="px-3 py-1 bg-gray-200 rounded text-sm"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <h3 className="font-medium text-gray-900">{floor.name}</h3>
                        <span className="text-xs text-gray-500">Order: {floor.order}</span>
                      </div>
                      <div className="flex gap-1">
                        <button
                          onClick={() => setEditingFloor(floor)}
                          className="text-blue-600 hover:text-blue-800 p-1"
                          title="Edit"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteFloor(floor)}
                          className="text-red-600 hover:text-red-800 p-1"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    <div className="mt-2">
                      {floor.floorMapImageUrl ? (
                        <div className="relative">
                          <img
                            src={floor.floorMapImageUrl}
                            alt={floor.name}
                            className="w-full h-32 object-contain rounded border bg-gray-50"
                          />
                          <div className="flex gap-2 mt-2">
                            <label className="cursor-pointer">
                              <input
                                type="file"
                                accept="image/png,image/jpeg,image/jpg"
                                className="hidden"
                                onChange={(e) => {
                                  const f = e.target.files?.[0];
                                  if (f && floor.id) handleUploadFloorMap(floor.id, f);
                                }}
                              />
                              <span className="text-xs text-blue-600 hover:underline">
                                Replace image
                              </span>
                            </label>
                            {onViewFloorMap && floor.id && (
                              <button
                                onClick={() => onViewFloorMap(floor.id!)}
                                className="text-xs text-primary-600 hover:underline"
                              >
                                View on map
                              </button>
                            )}
                          </div>
                        </div>
                      ) : (
                        <label className="block border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer hover:border-primary-400 hover:bg-gray-50">
                          <input
                            type="file"
                            accept="image/png,image/jpeg,image/jpg"
                            className="hidden"
                            disabled={uploadingFloorId === floor.id}
                            onChange={(e) => {
                              const f = e.target.files?.[0];
                              if (f && floor.id) handleUploadFloorMap(floor.id, f);
                            }}
                          />
                          {uploadingFloorId === floor.id ? (
                            <span className="text-sm text-gray-500">Uploading...</span>
                          ) : (
                            <>
                              <Upload className="w-8 h-8 mx-auto text-gray-400 mb-2" />
                              <span className="text-sm text-gray-500">
                                Upload floor plan (PNG/JPG)
                              </span>
                            </>
                          )}
                        </label>
                      )}
                    </div>
                  </>
                )}
              </div>
            ))}

            {isAdding ? (
              <div className="p-4 border border-gray-200 rounded-lg bg-gray-50">
                <input
                  type="text"
                  value={newFloorName}
                  onChange={(e) => setNewFloorName(e.target.value)}
                  placeholder="Floor name (e.g. Ground Floor)"
                  className="w-full px-3 py-2 border rounded text-sm mb-2"
                  autoFocus
                />
                <div className="flex gap-2">
                  <button
                    onClick={handleAddFloor}
                    className="px-3 py-2 bg-primary-600 text-white rounded text-sm"
                  >
                    Add
                  </button>
                  <button
                    onClick={() => { setIsAdding(false); setNewFloorName(''); }}
                    className="px-3 py-2 bg-gray-200 rounded text-sm"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <button
                onClick={() => { setIsAdding(true); setNewFloorOrder(floors.length); }}
                className="w-full p-4 border-2 border-dashed border-gray-300 rounded-lg text-gray-500 hover:border-primary-400 hover:text-primary-600 flex items-center justify-center gap-2"
              >
                <Plus className="w-5 h-5" />
                Add Floor
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default FloorManagementPanel;
